import React from 'react';
import { DeckDoc, DeckThemeStyle, PublicDeck, ScaledSlide } from '../../../components/decks/render';

// #region SampleEditPlayground
/**
 * Dev only: /d/__sample?mode=edit renders every sample slide in edit mode with local state, so the renderer's
 * in-place editing (text, items, rows, image picks) can be tried without the editor.
 */
export const SampleEditPlayground: React.FunctionComponent<{ deck: PublicDeck }> = ({ deck }): React.ReactElement => {
    const [doc, setDoc] = React.useState<DeckDoc>(deck.doc);
    const [selected, setSelected] = React.useState<{ slide: string; field: string | null } | null>(null);
    const [log, setLog] = React.useState<string[]>([]);
    const note = (line: string): void => setLog((l) => [line, ...l].slice(0, 6));

    const change = (slideId: string) => (key: string, value: unknown): void => {
        setDoc((d) => ({ ...d, slides: d.slides.map((s) => (s.id === slideId ? { ...s, fields: { ...s.fields, [key]: value as never } } : s)) }));
        note(`${slideId}.${key} = ${JSON.stringify(value).slice(0, 80)}`);
    };

    return (
        <div className="wd-themed min-h-screen bg-zinc-100 pb-24">
            <DeckThemeStyle theme={deck.context.theme} />
            <header className="sticky top-0 z-10 border-b border-zinc-200 bg-white/95 px-6 py-3 text-sm backdrop-blur">
                <p className="font-semibold">Renderer edit mode (sample)</p>
                <p className="truncate text-zinc-500">Selected: {selected ? `${selected.slide} → ${selected.field ?? '(slide)'}` : 'none'}{log.length ? ` · last change: ${log[0]}` : ''}</p>
            </header>
            <div className="mx-auto grid max-w-5xl gap-8 px-4 pt-8">
                {doc.slides.map((slide) => (
                    <div key={slide.id}>
                        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-zinc-500">{slide.layout}</p>
                        <ScaledSlide slide={slide} context={deck.context} mode="edit" selected={selected?.slide === slide.id}
                            onFieldChange={change(slide.id)}
                            onSelectField={(field) => { setSelected({ slide: slide.id, field }); note(`select ${slide.id}.${field ?? '(slide)'}`); }}
                            innerStyle={{ borderRadius: 24, overflow: 'hidden', boxShadow: '0 20px 60px -20px rgba(0,0,0,.25)' }} />
                    </div>
                ))}
            </div>
        </div>
    );
};
